package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.rememberPlatformFilePicker
import kotlinx.coroutines.launch
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddEditTrainingScreen(
    trainingItem: TrainingItem?,
    onBack: () -> Unit,
    onSaved: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    var title by remember { mutableStateOf(trainingItem?.title ?: "") }
    var description by remember { mutableStateOf(trainingItem?.description ?: "") }

    var categories by remember { mutableStateOf<List<TrainingCategory>>(emptyList()) }
    var selectedCategoryId by remember { mutableStateOf(trainingItem?.categoryId ?: "") }
    var categoryDropdownExpanded by remember { mutableStateOf(false) }

    val images = remember { mutableStateListOf<String>().apply { addAll(trainingItem?.images ?: emptyList()) } }
    var isUploading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var pendingImageIndex by remember { mutableStateOf<Int?>(null) }

    fun loadCategories() {
        scope.launch {
            repository.getTrainingCategories().onSuccess { list ->
                categories = list
                if (selectedCategoryId.isEmpty() && list.isNotEmpty()) {
                    selectedCategoryId = list.first().id
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        loadCategories()
    }

    val filePicker = rememberPlatformFilePicker { bytes, name ->
        scope.launch {
            isUploading = true
            try {
                val url = storageService.uploadFile(bytes, StorageArea.TRAINING, name)
                images.add(url)
            } catch (e: Throwable) {
                snackbarHostState.showSnackbar(e.message ?: "Upload failed")
            }
            isUploading = false
        }
    }

    val selectedCategory = categories.find { it.id == selectedCategoryId }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text(if (trainingItem == null) "Add Training Item" else "Edit Training Item") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    Button(
                        onClick = {
                            if (title.isNotBlank() && selectedCategoryId.isNotBlank()) {
                                val req = SaveTrainingItemRequest(
                                    id = trainingItem?.id,
                                    title = title.trim(),
                                    description = description.ifBlank { null },
                                    categoryId = selectedCategoryId,
                                    images = images.toList()
                                )
                                scope.launch {
                                    isSaving = true
                                    repository.saveTrainingItem(req).onSuccess {
                                        onSaved()
                                    }.onFailure {
                                        isSaving = false
                                        snackbarHostState.showSnackbar("Failed to save training item")
                                    }
                                }
                            }
                        },
                        enabled = title.isNotBlank() && selectedCategoryId.isNotBlank() && !isSaving,
                        modifier = Modifier.padding(end = 8.dp),
                        shape = MaterialTheme.shapes.small
                    ) {
                        if (isSaving) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), color = MaterialTheme.colorScheme.onPrimary)
                        } else {
                            Text("SAVE")
                        }
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedTextField(
                value = title,
                onValueChange = { title = it },
                label = { Text("Training Title *") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = MaterialTheme.shapes.small
            )

            // Category Dropdown
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Training Category *", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                ExposedDropdownMenuBox(
                    expanded = categoryDropdownExpanded,
                    onExpandedChange = { categoryDropdownExpanded = !categoryDropdownExpanded }
                ) {
                    OutlinedTextField(
                        value = selectedCategory?.let { "${it.categoryName}${it.subCategoryName?.let { sub -> " -> $sub" } ?: ""}" } ?: "Select Category",
                        onValueChange = {},
                        readOnly = true,
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = categoryDropdownExpanded) },
                        modifier = Modifier.fillMaxWidth().menuAnchor(),
                        shape = MaterialTheme.shapes.small
                    )
                    ExposedDropdownMenu(
                        expanded = categoryDropdownExpanded,
                        onDismissRequest = { categoryDropdownExpanded = false }
                    ) {
                        if (categories.isEmpty()) {
                            DropdownMenuItem(
                                text = { Text("No categories available") },
                                onClick = { categoryDropdownExpanded = false }
                            )
                        } else {
                            categories.forEach { cat ->
                                DropdownMenuItem(
                                    text = { Text("${cat.categoryName}${cat.subCategoryName?.let { " -> $it" } ?: ""}") },
                                    onClick = {
                                        selectedCategoryId = cat.id
                                        categoryDropdownExpanded = false
                                    }
                                )
                            }
                        }
                    }
                }
            }

            // Description Section
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Description / Training Notes", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = { Text("Enter detailed training description, instructions, or resources...") },
                    modifier = Modifier.fillMaxWidth().height(150.dp),
                    minLines = 5,
                    maxLines = 10,
                    shape = MaterialTheme.shapes.small
                )
            }

            // Media Section
            Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("Attachments (video, PDF, image, document) (${images.size})", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                OutlinedButton(
                    onClick = { filePicker.pickFile() },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !isUploading
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Image(
                            painter = painterResource(Res.drawable.folder),
                            contentDescription = "Choose file",
                            modifier = Modifier.size(20.dp),
                            contentScale = ContentScale.Fit
                        )
                        Text(if (isUploading) "Uploading..." else "Choose file")
                    }
                }
            }

            if (isUploading) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp))
                    Text("Uploading media...", style = MaterialTheme.typography.bodySmall)
                }
            }
            if (images.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    images.forEachIndexed { index, imgUrl ->
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = MaterialTheme.shapes.small,
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(8.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.weight(1f)) {
                                    if (isImageFileUrl(imgUrl)) {
                                        Box(modifier = Modifier.size(50.dp)) {
                                            RemoteImageView(url = imgUrl, modifier = Modifier.fillMaxSize())
                                        }
                                    }
                                    Text(
                                        text = displayFileName(imgUrl),
                                        style = MaterialTheme.typography.bodyMedium,
                                        maxLines = 2
                                    )
                                }
                                TextButton(onClick = { pendingImageIndex = index }) {
                                    Text("Remove", color = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    pendingImageIndex?.let { index ->
        ConfirmDeleteDialog(
            title = "Remove media?",
            message = "Remove ${displayFileName(images.getOrNull(index).orEmpty())}?",
            confirmLabel = "Remove",
            onConfirm = {
                if (index in images.indices) images.removeAt(index)
                pendingImageIndex = null
            },
            onDismiss = { pendingImageIndex = null }
        )
    }
}
